import { ProbeSequence } from '../../features/probe/probe-details/_core/types/predefined-probe-overview.interface';

function reverseComplement(sequence: string | null | undefined): string {
    if (!sequence) {
        return '';
    }

    const complementMap: { [key: string]: string } = {
        A: 'T',
        T: 'A',
        C: 'G',
        G: 'C',
    };

    return sequence
        .split('')
        .map(base => complementMap[base] || base)
        .reverse()
        .join('');
}

/**
 * Detects links in free text — mask before DNA highlighting so primers cannot alter URLs.
 * - http:// and https:// (scheme optional in spirit: both accepted; case-insensitive)
 * - protocol-relative //example.com/...
 * - www.example.com/... (host must contain a dot so short tokens like www.a are skipped)
 */
const SEQUENCE_LINK_REGEX = /https?:\/\/[^\s<>"']+|(?<!:)\/\/[^\s<>"']+|\bwww\.[^\s<>"']+/gi;

const URL_PLACEHOLDER_PREFIX = '__TYX_SEQ_LINK_';
const URL_PLACEHOLDER_SUFFIX = '__';

interface SequenceLinkTarget {
    /** href as sent to the browser (scheme added for // and www. only; otherwise exact from API). */
    href: string;
    /** Link text exactly as in the sequence string from the API. */
    anchorText: string;
}

function escapeHtmlAttribute(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeHtmlText(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function resolveSequenceLink(match: string): SequenceLinkTarget | null {
    if (/^https?:\/\//i.test(match)) {
        return { href: match, anchorText: match };
    }
    if (match.startsWith('//')) {
        return { href: `https:${match}`, anchorText: match };
    }
    if (/^www\./i.test(match)) {
        const afterWww = match.slice(4);
        if (!afterWww.includes('.')) {
            return null;
        }
        return { href: `https://${match}`, anchorText: match };
    }
    return null;
}

function maskSequenceUrls(sequence: string): { masked: string; links: SequenceLinkTarget[] } {
    const links: SequenceLinkTarget[] = [];
    const masked = sequence.replace(SEQUENCE_LINK_REGEX, match => {
        const resolved = resolveSequenceLink(match);
        if (!resolved) {
            return match;
        }
        const id = links.length;
        links.push(resolved);
        return `${URL_PLACEHOLDER_PREFIX}${id}${URL_PLACEHOLDER_SUFFIX}`;
    });
    return { masked, links };
}

function unmaskSequenceUrls(html: string, links: SequenceLinkTarget[]): string {
    let out = html;
    links.forEach((link, i) => {
        const token = `${URL_PLACEHOLDER_PREFIX}${i}${URL_PLACEHOLDER_SUFFIX}`;
        const anchor = `<a href="${escapeHtmlAttribute(link.href)}" target="_blank" rel="noopener noreferrer">${escapeHtmlText(
            link.anchorText
        )}</a>`;
        out = out.split(token).join(anchor);
    });
    return out;
}

/**
 * Prepares a probe sequence string for API payload: DNA/non-link text in UPPERCASE;
 * detected URL segments (same rules as display) stay exactly as entered.
 */
function normalizeProbeSequenceForPayload(value: string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const { masked, links } = maskSequenceUrls(value);
    const upperMasked = masked.toUpperCase();
    let out = upperMasked;
    links.forEach((link, i) => {
        const token = `${URL_PLACEHOLDER_PREFIX}${i}${URL_PLACEHOLDER_SUFFIX}`;
        out = out.split(token.toUpperCase()).join(link.anchorText);
    });
    return out;
}

function highlightProbeSequence(probeSequence: ProbeSequence | null | undefined): string {
    if (!probeSequence || !probeSequence.sequence) {
        return '';
    }

    const { masked, links } = maskSequenceUrls(probeSequence.sequence);
    let highlighted = masked;

    const forwardPrimerSequence = probeSequence.forwardPrimer;
    const reversePrimerSequenceReverseComplement = reverseComplement(probeSequence.reversePrimer);
    const reporter1Sequence = probeSequence.reporter1;
    const reporter1SequenceReverseComplement = reverseComplement(probeSequence.reporter1);

    if (forwardPrimerSequence && highlighted.includes(forwardPrimerSequence)) {
        highlighted = highlighted.replace(
            forwardPrimerSequence,
            '<span class="label label-warning">' + forwardPrimerSequence + '</span>'
        );
    }

    if (reversePrimerSequenceReverseComplement && highlighted.includes(reversePrimerSequenceReverseComplement)) {
        highlighted = highlighted.replace(
            reversePrimerSequenceReverseComplement,
            '<span class="label label-warning">' + reversePrimerSequenceReverseComplement + '</span>'
        );
    }

    if (reporter1Sequence && highlighted.includes(reporter1Sequence)) {
        highlighted = highlighted.replace(
            reporter1Sequence,
            '<span class="label label-important">' + reporter1Sequence + '</span>'
        );
    }

    if (reporter1SequenceReverseComplement && highlighted.includes(reporter1SequenceReverseComplement)) {
        highlighted = highlighted.replace(
            reporter1SequenceReverseComplement,
            '<span class="label label-important">' + reporter1SequenceReverseComplement + '</span>'
        );
    }

    return `<span class="probe-sequence-highlight">${unmaskSequenceUrls(highlighted, links)}</span>`;
}

export interface ProbeSequenceMatchInput {
    forwardPrimerSeq: string;
    reversePrimerSeq: string;
    reporter1Sequence: string;
}

/**
 * returns true if the sequence matches:
 * forwardPrimerMatch && reversePrimerReverseComplementMatch && (reporter1Match || reporter1ReverseComplementMatch)
 * -- used for ABI file import
 */
function doesProbeSequenceMatch(
    item: ProbeSequenceMatchInput | null | undefined,
    sequence: string | null | undefined
): boolean {
    if (!item || !sequence) {
        return false;
    }

    const forwardPrimer = (item.forwardPrimerSeq || '').trim();
    const reversePrimer = (item.reversePrimerSeq || '').trim();
    const reporter1 = (item.reporter1Sequence || '').trim();

    const forwardPrimerMatch = !forwardPrimer || sequence.includes(forwardPrimer);
    const reversePrimerReverseComplement = reverseComplement(reversePrimer);
    const reversePrimerReverseComplementMatch =
        !reversePrimerReverseComplement || sequence.includes(reversePrimerReverseComplement);
    const reporter1Match = !reporter1 || sequence.includes(reporter1);
    const reporter1ReverseComplement = reverseComplement(reporter1);
    const reporter1ReverseComplementMatch =
        !reporter1ReverseComplement || sequence.includes(reporter1ReverseComplement);

    return (
        forwardPrimerMatch && reversePrimerReverseComplementMatch && (reporter1Match || reporter1ReverseComplementMatch)
    );
}

export const ProbeSequenceHelper = {
    reverseComplement,
    highlightProbeSequence,
    normalizeProbeSequenceForPayload,
    doesProbeSequenceMatch,
};
